// #12 Aviso FLOTANTE del chat de EQUIPO. El badge con el número en la cabecera a veces no se ve
// (estás en otra pantalla, en móvil, mirando otra cosa), así que un mensaje nuevo pasaba
// desapercibido. Esta tarjeta tipo toast aparece unos segundos en una esquina, dice de qué
// conversación es y un extracto, y al pulsarla lleva al chat.
//
// Detección: mismo dato que usa el ChatWidget (react-query sobre /chat/canales). Guardamos el
// nº de no-leídos de cada canal de equipo y, cuando ESE contador SUBE (mensaje nuevo de otra
// persona) y NO estamos en /chat, mostramos el aviso. Los no-leídos que da el backend ya excluyen
// los mensajes PROPIOS (msg.agente_id IS DISTINCT FROM yo), así que esto nunca salta por lo que
// escribo yo. El soporte tiene su propio globo → aquí solo miramos el chat del equipo (tipo≠soporte).
import { useState, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '../services/api'
import { usePermisos } from '../hooks/usePermisos'
import { Ico } from './ui'
// t319 · con el panel de la ayuda abierto, este aviso se pliega en una pastilla junto al globo (utils/panelAyuda).
import { estadoPanelAyuda, suscribirPanelAyuda, sitioDeLosAvisos, PASTILLA_AVISO } from '../utils/panelAyuda'
// t515 · B-2248 · «Mensaje nuevo en el chat del equipo», canal «En pantalla» (Notificaciones → Configurar y el Perfil).
import { meLlegaDe, CLAVE_MIS_AVISOS, RUTA_MIS_AVISOS } from '../utils/misAvisos'
import { t as i18nT } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// Avatar de DM (base64 → data URI), igual criterio que el ChatWidget.
const avatarSrc = (b64) => `data:image/${(b64 || '').startsWith('iVBOR') ? 'png' : 'jpeg'};base64,${b64}`
const inicial = (n) => (n || '?').trim()[0]?.toUpperCase() || '?'

// B-830 · `visible`: si el chat está a la vista de esta persona, con la MISMA regla que su botón de la cabecera
// (Layout · moduloVisible). El permiso `chat` lo trae de fábrica todo el equipo, así que por sí solo no bastaba: a quien
// su rol tiene el chat oculto le seguían saliendo los avisos flotantes.
export default function ChatNotifFlotante({ visible = true }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { permiso } = usePermisos()
  const puede = permiso('chat') && visible !== false
  // Nunca molestamos si el usuario ya está EN el chat (lo está leyendo) ni sin permiso de chat.
  // Mismo criterio que Layout: la ruta exacta /chat o cualquier subruta /chat/...
  const enChat = location.pathname === '/chat' || location.pathname.startsWith('/chat/')

  const [aviso, setAviso] = useState(null)   // { id, titulo, texto, avatar, esDM } o null
  // Línea base de no-leídos por canal. null = aún no fijada (primera foto o recién salidos de /chat):
  // esa primera foto NO avisa, solo establece el punto de partida para no notificar el historial.
  const prevRef = useRef(null)

  // MISMA queryKey que el ChatWidget → comparten caché: si el widget ya está refrescando, leemos su
  // dato sin pedir de más; si está cerrado, este refetchInterval mantiene vivo el sondeo (que es
  // justo cuando el badge no se ve y hace falta el aviso). Se apaga dentro de /chat y sin permiso.
  // POR QUE LOS AVISOS LLEGABAN TARDE. Dos motivos, y el segundo era el gordo:
  //   · el sondeo iba cada 15 s, asi que un mensaje podia tardar eso en aparecer;
  //   · y, sobre todo, React Query NO sondea con la pestaña en segundo plano. Trabajando con la app
  //     en otra pestaña —que es lo normal— no llegaba NADA hasta volver, y al volver habia que
  //     esperar al siguiente tick: de ahi la sensacion de "desfasadas".
  // Se arregla por los dos lados: 8 s de sondeo, y una lectura INMEDIATA al volver a la pestaña. No
  // se sondea en segundo plano a proposito: gastaria bateria y red para avisar de algo que nadie
  // esta mirando; lo que importa es que al volver este al dia al instante.
  const { data: canales = [] } = useQuery({
    queryKey: ['chat-canales'],
    queryFn: () => api.get('/chat/canales').then(r => r.data).catch(() => []),
    enabled: puede && !enChat,
    refetchInterval: puede && !enChat ? 8000 : false,
    refetchOnWindowFocus: true,
    staleTime: 0,
  })

  // t515 · si la clínica le ha quitado este aviso en pantalla (o se lo ha quitado la persona), no se pinta. Sin respuesta, sí.
  const { data: misAvisos } = useQuery({
    queryKey: CLAVE_MIS_AVISOS,
    queryFn: () => api.get(RUTA_MIS_AVISOS).then(r => r.data).catch(() => null),
    enabled: puede, staleTime: 5 * 60 * 1000,
  })
  const enPantalla = meLlegaDe(misAvisos, 'comunicacion.mensaje', 'ventana')

  // Al entrar en /chat: quita cualquier aviso visible y borra la base, de modo que al SALIR la
  // primera lectura vuelva a fijar la línea base sin disparar avisos de lo que se leyó dentro.
  useEffect(() => { if (enChat) { setAviso(null); prevRef.current = null } }, [enChat])

  // Detección del incremento. TanStack v5 no tiene onSuccess → reaccionamos con useEffect a `canales`.
  useEffect(() => {
    if (!puede) return
    // Foto actual: solo canales de EQUIPO (el soporte va por el globo de ayuda).
    const actual = {}
    for (const c of canales) { if (c.tipo !== 'soporte') actual[c.id] = Number(c.no_leidos) || 0 }
    const prev = prevRef.current
    // Primera foto (o recién salidos de /chat) o estando en /chat: solo fijamos base, sin avisar.
    if (prev === null || enChat) { prevRef.current = actual; return }
    // La lista viene ordenada por ultimo_at DESC, así que el PRIMER canal cuyo contador sube es el
    // del mensaje más reciente: ese es el que anunciamos (un aviso a la vez).
    let nuevo = null
    for (const c of canales) {
      if (c.tipo === 'soporte') continue
      const antes = prev[c.id] || 0
      const ahora = Number(c.no_leidos) || 0
      if (ahora > antes && ahora > 0) { nuevo = c; break }
    }
    if (nuevo) {
      setAviso({
        id: nuevo.id,
        titulo: nuevo.titulo || nuevo.nombre || i18nT('components.ChatNotifFlotante.mensaje_nuevo', 'Mensaje nuevo'),
        texto: (nuevo.ultimo || '').trim(),
        avatar: nuevo.dm_usuario_avatar || null,
        esDM: nuevo.tipo === 'dm',
        // clave para forzar re-render/re-timer aunque sea el mismo canal dos veces seguidas
        _k: Date.now(),
      })
    }
    prevRef.current = actual
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canales, enChat, puede])

  // Se cierra solo. Cada aviso nuevo (objeto distinto) reinicia el temporizador.
  useEffect(() => {
    if (!aviso) return
    const t = setTimeout(() => setAviso(null), 6500)
    return () => clearTimeout(t)
  }, [aviso])

  // t319 · EL PANEL DE LA AYUDA, ABIERTO: este aviso (capa 2500) salía ENCIMA de su cabecera, su aspa y su buscador (2000).
  // Con el panel abierto se pliega en la pastilla de al lado del globo, fuera del panel; con el panel a pantalla completa
  // no queda sitio libre y no se pinta (utils/panelAyuda). Lo demás no cambia: se cierra solo, con su aspa o al pulsarlo.
  const [panelAyuda, setPanelAyuda] = useState(estadoPanelAyuda)
  useEffect(() => suscribirPanelAyuda(setPanelAyuda), [])
  const sitioAviso = sitioDeLosAvisos(panelAyuda)
  const pastilla = sitioAviso === 'pastilla'

  if (!aviso || enChat || !puede || !enPantalla || sitioAviso === 'oculto') return null

  // B-207 · A LA CONVERSACIÓN, no al chat en general. La campana, el push y este aviso tienen que
  // llevar al MISMO sitio; dejarlo en '/chat' obligaba a buscar a mano de quién era el mensaje.
  const irAlChat = () => { setAviso(null); navigate(aviso?.id ? '/chat?c=' + aviso.id : '/chat') }

  return (
    <>
      <div role="button" tabIndex={0} onClick={irAlChat}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); irAlChat() } }}
        title={i18nT('components.ChatNotifFlotante.ir_al_chat_del_equipo', 'Ir al chat del equipo')}
        style={{
          position: 'fixed',
          // Esquina superior derecha, DEBAJO de la cabecera (58px) y del notch. Deja libre el
          // centro (toasts y NotifPersonal) y el pie derecho (globo de ayuda / panel del widget).
          top: 'calc(env(safe-area-inset-top, 0px) + 70px)',
          right: '16px',
          width: 'min(370px, calc((100vw / var(--z, 1)) - 32px))',
          // t302 · la capa de los avisos que flotan (index.css, «LAS CAPAS DE LA APLICACIÓN»): sale justo donde se abren el
          // menú del avatar y la campana, y lo que se acaba de abrir queda encima.
          zIndex: 'var(--capa-aviso-flotante)',
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '12px 14px',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderLeft: '4px solid var(--primary)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          cursor: 'pointer',
          animation: 'chatnotif-in .28s cubic-bezier(.16,1,.3,1)',
          // t319 · la pastilla: abajo, a la izquierda del globo de la ayuda, de una línea (utils/panelAyuda · PASTILLA_AVISO).
          ...(pastilla ? { ...PASTILLA_AVISO, gap: '9px', padding: '6px 6px 6px 9px', borderRadius: '14px' } : {}),
        }} data-aviso-pastilla={pastilla ? '' : undefined}>
        {/* Avatar del compañero (DM) o burbuja de chat (grupo). */}
        {aviso.esDM ? (
          <span style={{ width: pastilla ? '30px' : '40px', height: pastilla ? '30px' : '40px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: 'color-mix(in srgb, var(--primary) 16%, transparent)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '15px' }}>
            {aviso.avatar ? <img src={avatarSrc(aviso.avatar)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : inicial(aviso.titulo)}
          </span>
        ) : (
          <span style={{ width: pastilla ? '30px' : '40px', height: pastilla ? '30px' : '40px', borderRadius: pastilla ? '9px' : '11px', flexShrink: 0, background: 'color-mix(in srgb, var(--primary) 14%, transparent)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 8h2a2 2 0 0 1 2 2v11l-4-4H9a2 2 0 0 1-2-2v-1" /><path d="M13 2H5a2 2 0 0 0-2 2v9l4-4h6a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z" /></svg>
          </span>
        )}
        <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <b style={{ fontSize: '13.5px', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{aviso.titulo}</b>
            <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.4px', flexShrink: 0 }}>{i18nT('components.ChatNotifFlotante.chat_equipo', 'Chat equipo')}</span>
          </span>
          <span style={{ fontSize: '12.5px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: pastilla ? 1 : 2, WebkitBoxOrient: 'vertical', lineHeight: pastilla ? 1.3 : 1.35 }}>
            {aviso.texto || i18nT('components.ChatNotifFlotante.mensaje_nuevo', 'Mensaje nuevo')}
          </span>
        </span>
        <button onClick={e => { e.stopPropagation(); setAviso(null) }} title={i18nT('components.ChatNotifFlotante.descartar', 'Descartar')} aria-label={i18nT('components.ChatNotifFlotante.descartar', 'Descartar')}
          style={{ width: '28px', height: '28px', borderRadius: '50%', border: 'none', background: 'var(--surface-2)', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Ico n="x" s={15} />
        </button>
      </div>
      <style>{`@keyframes chatnotif-in{from{opacity:0;transform:translateY(-14px) scale(.97)}to{opacity:1;transform:translateY(0) scale(1)}}`}</style>
    </>
  )
}
